import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import type { ApiResponse } from '../types';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v2',
  timeout: 30000, // 默认30秒，异步任务接口不需要长超时
  headers: {
    'Content-Type': 'application/json',
  },
});

// 获取或创建访客ID
function getGuestId(): string {
  let guestId = localStorage.getItem('guestId');
  if (!guestId) {
    // 生成一个随机的访客ID
    guestId = 'guest-' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('guestId', guestId);
  }
  return guestId;
}

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // 未登录用户，添加访客ID
      config.headers['X-Guest-ID'] = getGuestId();
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized - 使用相对路径，避免跳转到根目录的login
      localStorage.removeItem('token');
      // 不自动跳转，让调用方处理
      console.warn('API 401 Unauthorized');
    }
    return Promise.reject(error);
  }
);

// Generic API methods
export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.get<ApiResponse<T>>(url, config);
  return response.data.data as T;
}

export async function post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.post<ApiResponse<T>>(url, data, config);
  return response.data.data as T;
}

export async function put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.put<ApiResponse<T>>(url, data, config);
  return response.data.data as T;
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.delete<ApiResponse<T>>(url, config);
  return response.data.data as T;
}

// Chat API
export const chatApi = {
  sendMessage: (message: string, sessionId?: string) =>
    post('/chat/message', { message, sessionId }),
  
  getSessions: () =>
    get('/chat/sessions'),
  
  getSession: (sessionId: string) =>
    get(`/chat/sessions/${sessionId}`),
  
  createSession: () =>
    post('/chat/sessions'),
  
  deleteSession: (sessionId: string) =>
    del(`/chat/sessions/${sessionId}`),
};

// Design API
export interface GenerateDesignRequest {
  prompt: string;
  templateId?: string;
  themeConfig?: {
    primaryColor?: string;
    secondaryColor?: string;
    backgroundColor?: string;
    textColor?: string;
    fontFamily?: string;
    borderRadius?: string;
  };
  currentHtml?: string;
  model?: string;
}

export interface GenerateDesignResponse {
  success: boolean;
  html: string;
  title?: string;
  description?: string;
  error?: string;
}

// 异步任务相关类型
export type DesignTaskStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';

export interface DesignTask {
  id: string;
  status: DesignTaskStatus;
  html?: string;
  title?: string;
  description?: string;
  error?: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  processingTime?: number;
  retryCount?: number;
}

export interface CreateTaskResponse {
  id: string;
  status: DesignTaskStatus;
  createdAt: string;
}

export const designApi = {
  // 同步生成（已弃用，保留向后兼容）
  generateDesign: async (data: GenerateDesignRequest): Promise<GenerateDesignResponse> => {
    const response = await apiClient.post('/design/generate', data);
    return response.data as GenerateDesignResponse;
  },
  
  // ========== 异步任务接口 ==========
  
  /**
   * 创建异步设计任务
   */
  createTask: async (data: GenerateDesignRequest): Promise<CreateTaskResponse> => {
    const response = await apiClient.post('/design/tasks', data);
    return response.data as CreateTaskResponse;
  },
  
  /**
   * 获取任务状态
   */
  getTaskStatus: async (taskId: string): Promise<DesignTask> => {
    const response = await apiClient.get(`/design/tasks/${taskId}`);
    return response.data as DesignTask;
  },
  
  /**
   * 获取任务列表
   */
  getTaskList: async (params?: { status?: DesignTaskStatus; page?: number; limit?: number }): Promise<{ tasks: DesignTask[]; total: number }> => {
    const response = await apiClient.get('/design/tasks', { params });
    return response.data as { tasks: DesignTask[]; total: number };
  },
  
  /**
   * 取消任务
   */
  cancelTask: async (taskId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(`/design/tasks/${taskId}`);
    return response.data as { success: boolean; message: string };
  },
  
  // ========== 原有接口 ==========
  
  saveDesign: (design: unknown) =>
    post('/design/save', design),
  
  getDesigns: () =>
    get('/design/list'),
  
  getDesign: (id: string) =>
    get(`/design/${id}`),
};

export default apiClient;
